// components/ReusableInput.tsx
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface TextInputProps {
  name: string;
  label?: string;
  type?: string;
  placeholder?: string;
  isRequired?: boolean;
  size?: "half" | "full";
  disabled?: boolean;
  onKeyDown?: (e: any) => void;
  customError?: any;
}

const TextInput: FC<TextInputProps> = ({ name, label, type = "text", placeholder, isRequired, size = "half", disabled, onKeyDown, customError }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      ["Backspace", "Delete", "Tab", "Escape", "Enter", "."].includes(e.key) ||
      (e.ctrlKey === true && ["a", "c", "v", "x"].includes(e.key)) ||
      /^\d$/.test(e.key)
    ) {
      return;
    }
    e.preventDefault();
  };

  return (
    <div className="flex flex-col gap-1 mb-3 w-full">
      {label && (
        <label htmlFor={name} className="text-sm font-medium text-text mb-0">
          {t(label)}
          {isRequired && <span className="text-danger"> *</span>}
        </label>
      )}
      <div className={`${size === "half" ? "md:w-1/2 w-full" : "w-full"} relative mt-1`}>
        <Controller
          name={name}
          control={control}
          render={({ field }) =>
            type === "textarea" ? (
              <textarea
                id={name}
                placeholder={placeholder}
                disabled={disabled}
                {...field}
                className={`transition300 bg-inputBg block px-3 py-2 w-full border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm ${
                  errors[name] ? "border-danger" : "border-inputBg"
                }`}
              />
            ) : (
              <input
                id={name}
                type={type === "password" && showPassword ? "text" : type}
                placeholder={placeholder}
                disabled={disabled}
                onKeyDown={type === "number" ? handleNumberInput : onKeyDown}
                step={type === "number" ? "any" : undefined}
                {...field}
                className={`transition300 bg-inputBg block px-3 py-2 w-full border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm ${
                  errors[name] ? "border-danger" : "border-inputBg"
                }`}
              />
            )
          }
        />
        {type === "password" && (
          <FontAwesomeIcon
            icon={showPassword ? faEyeSlash : faEye}
            onClick={togglePasswordVisibility}
            className="absolute top-1/2 -translate-y-1/2 right-0 pr-3 flex items-center text-mute cursor-pointer"
          />
        )}
      </div>

      {customError && <p className="text-sm text-danger -mt-1">{customError}</p>}
      {errors[name] && <p className="text-sm text-danger -mt-1">{String(errors[name]?.message)}</p>}
    </div>
  );
};

export default TextInput;
